<script setup>
import { onMounted, onBeforeUnmount, computed, ref } from "vue";
import { useRoute } from "vue-router";
import router from '@/router/index.js';
import { consumeOfflineReturnPath, toAppUrl } from '@functions/offlineReturnPath';

const route = useRoute()

// コンポーネントに渡されたpropsの取得
const props = defineProps({
  title: String,
  explanation: String,
  errorCode: {
    type: [String, Number],
    default: null
  }
});

// エラーコード別のデフォルトメッセージ
const errorMessages = {
  400: {
    title: '指定されたページを表示できません。'
    , explanation: '申し訳ございませんが、リクエストを正しく処理できませんでした。送信内容が正確であることを確認し、もう一度お試しください。'
  },
  404: {
    title: 'お探しのページは見つかりませんでした。',
    explanation: 'お探しのページは移動もしくは削除されたか、URLが誤っている可能性があります。最新の情報はトップページよりご覧ください。'
  },
  500: {
    title: 'サーバーエラー。',
    explanation: 'サーバーで問題が発生しました。現在、サービス提供ができない可能性があります。しばらくしてから再度アクセスしてください。'
  },
  offline: {
    title: 'インターネットに接続できません。',
    explanation: 'オンラインの状態でアプリを開いてください。'
  },
  default: {
    title: 'エラーが発生しました。',
    explanation: '申し訳ございません。エラーが発生しました。'
  }
}

const getExplicitErrorCode = () => props.errorCode || route.query.errorCode

// WiFi 復帰後もオフライン表示を維持し、再試行成功まで 404 に切り替えない
const isOfflineMode = ref(
  String(getExplicitErrorCode() || '') === 'offline' ||
  (!getExplicitErrorCode() && !navigator.onLine)
)

const isRetrying = ref(false)

// エラーコードを決定（props > query > オフラインラッチ時は offline > 404をデフォルト）
const errorCode = computed(() => {
  const explicit = getExplicitErrorCode()
  if (explicit) return explicit
  if (isOfflineMode.value) return 'offline'
  return '404'
})

const isOfflineError = computed(() => String(errorCode.value) === 'offline')

// 表示するメッセージを決定
const displayTitle = computed(() => {
  // 1. props で指定された title
  if (props.title) return props.title
  
  // 2. query で指定された title
  if (route.query.title) return route.query.title
  
  // 3. エラーコード別のデフォルト
  const code = String(errorCode.value)
  return errorMessages[code]?.title || errorMessages.default.title
})

const displayExplanation = computed(() => {
  // 1. props で指定された explanation
  if (props.explanation) return props.explanation
  
  // 2. query で指定された explanation (message としても受け取れる)
  if (route.query.explanation) return route.query.explanation
  if (route.query.message) return route.query.message
  
  // 3. エラーコード別のデフォルト
  const code = String(errorCode.value)
  return errorMessages[code]?.explanation || errorMessages.default.explanation
})

const onRetry = () => {
  if (isRetrying.value) return
  isRetrying.value = true

  if (!navigator.onLine) {
    setTimeout(() => {
      isRetrying.value = false
    }, 500)
    return
  }

  const returnPath = consumeOfflineReturnPath(router) ?? '/';
  window.location.replace(toAppUrl(returnPath));
}

const onOffline = () => {
  if (!getExplicitErrorCode()) {
    isOfflineMode.value = true
  }
}

const onOnline = () => {
  if (isOfflineMode.value) {
    onRetry()
  }
}

onMounted(() => {
  window.addEventListener('online', onOnline)
  window.addEventListener('offline', onOffline)
})

onBeforeUnmount(() => {
  window.removeEventListener('online', onOnline)
  window.removeEventListener('offline', onOffline)
})
</script>

<template>
    <div class="error_bg1"></div>
    <div id="contents1" class="flx1">
      <div class="error_cnt1">
        <div class="block1">
          <p class="copy1">{{ displayTitle }}</p>
          <p class="copy2">{{ displayExplanation }}</p>
          <button
            v-if="isOfflineError"
            type="button"
            class="btn com_btn3 cl1"
            :disabled="isRetrying"
            @click="onRetry"
          >
            再試行
          </button>
          <router-link v-else to="/" class="btn com_btn3 cl1">トップページへ</router-link>
        </div>
      </div>
    </div>
</template>
